<template>
  <NoteSection
    label="Past Medical History"
    required
    :highlight-required="isSectionIncomplete"
    :form-validated="formValidated"
  >
  <!-- Old display method, changed to multi-select list box for higher user input speed
    <select-box
      v-for="(item, i) in section.medical_histories"
      :key="`item-${i}-${section.medical_histories.length}`"
      v-model="section.medical_histories[i]"
      :items="medicalHistories"
      :append-icon="icons.mdiCloseThick"
      label="Select from list"
      class="col-sm-4 past-medical-history"
      clearable="false"
      hide-details
      return-object
      @click:append="section.medical_histories.splice(i, 1)"
    ></select-box>
    <div class="col-sm-4">
      <btn
        label="Add Medical History"
        class="past-medical-history"
        :icon="icons.mdiTablePlus"
        :disabled="isSigned"
        @click="section.medical_histories.push({id:null})"
      ></btn>
    </div>
    -->
    <auto-complete-box
      v-if="section"
      v-model="section.medical_histories"
      :items="sortedMedicalHistories"
      label="Choose past medical histories"
      class="col-sm-12"
      multiple
      return-object
      :required="isMedicalHistoriesRequired"
    ></auto-complete-box>
    <text-area
      v-model="section.medical_histories_other"
      label="Enter other past medical history..."
      class="col-sm-12"
      counter="8200"
      maxlength="8200"
    ></text-area>
    <checkbox
      v-if="isHistoryDiabetes"
      v-model="section.neuropathy"
      label="Neuropathy"
      class="col-sm-12"
      disable-label-click
    ></checkbox>

    <v-container
      v-if="isHistoryDiabetes && section.neuropathy"
      fluid
    >
      <v-row class="mb-4">
        <v-subheader class="ml-4">
          #1
        </v-subheader>
        <select-box
          v-model="section.neuropathy_type"
          :items="neuropathyTypeOptions1"
          label="Neuropathy Type"
          class="col-sm-5 mr-4"
          required
          clearable
          hide-details
        />
        <select-box
          v-model="section.neuropathy_severity"
          :items="neuropathySeverities"
          label="Neuropathy Severity"
          class="col-sm-5 mr-4"
          :disabled="!section.neuropathy_type"
          :required="!!section.neuropathy_type"
          hide-details
        />
      </v-row>

      <v-row
        v-if="section.neuropathy_type"
        class="mb-4"
      >
        <v-subheader class="ml-4">
          #2
        </v-subheader>
        <select-box
          v-model="section.neuropathy_type_2"
          :items="neuropathyTypeOptions2"
          label="Neuropathy Type"
          class="col-sm-5 mr-4"
          clearable
          hide-details
        />
        <select-box
          v-model="section.neuropathy_severity_2"
          :items="neuropathySeverities"
          label="Neuropathy Severity"
          class="col-sm-5"
          :disabled="!section.neuropathy_type_2"
          :required="!!section.neuropathy_type_2"
          hide-details
        />
      </v-row>

      <v-row
        v-if="section.neuropathy_type_2"
        class="mb-4"
      >
        <v-subheader class="ml-4">
          #3
        </v-subheader>
        <select-box
          v-model="section.neuropathy_type_3"
          :items="neuropathyTypeOptions3"
          label="Neuropathy Type"
          class="col-sm-5 mr-4"
          clearable
          hide-details
        />
        <select-box
          v-model="section.neuropathy_severity_3"
          :items="neuropathySeverities"
          label="Neuropathy Severity"
          class="col-sm-5"
          :disabled="!section.neuropathy_type_3"
          :required="!!section.neuropathy_type_3"
          hide-details
        />
      </v-row>
    </v-container>

  </NoteSection>
</template>

<script>
import { mdiCloseThick, mdiTablePlus } from '@mdi/js';
import { mapGetters } from 'vuex';

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    medicalHistoriesOther: {
      type: String,
      default: null,
    },
    neuropathy: {
      type: [Boolean, Number],
      default: null,
    },
    neuropathyType: {
      type: String,
      default: null,
    },
    neuropathySeverity: {
      type: String,
      default: null,
    },
    neuropathyTypeTwo: {
      type: String,
      default: null,
    },
    neuropathySeverityTwo: {
      type: String,
      default: null,
    },
    neuropathyTypeThree: {
      type: String,
      default: null,
    },
    neuropathySeverityThree: {
      type: String,
      default: null,
    },
    isSigned: {
      type: Boolean,
      default: false,
    },
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      section: {
        medical_histories: [...(this.value || [])],
        medical_histories_other: this.medicalHistoriesOther,
        neuropathy: this.neuropathy,
        neuropathy_type: this.neuropathyType,
        neuropathy_severity: this.neuropathySeverity,
        neuropathy_type_2: this.neuropathyTypeTwo,
        neuropathy_severity_2: this.neuropathySeverityTwo,
        neuropathy_type_3: this.neuropathyTypeThree,
        neuropathy_severity_3: this.neuropathySeverityThree,
      },
      icons: { mdiTablePlus, mdiCloseThick },
    }
  },
  computed: {
    ...mapGetters('baseData', ['query']),
    ...mapGetters('encounters', ['medicalHistories', 'neuropathyTypes', 'neuropathySeverities']),
    sortedMedicalHistories() {
      return [...this.medicalHistories].sort((a, b) => a.title.localeCompare(b.title))
    },
    isHistoryDiabetes() {
      return this.section.medical_histories.some(x => x.id === 12)
      || this.section.medical_histories.some(x => x.id === 13)
      || this.$custom.strContains(this.section.medical_histories_other, 'diabetes')
    },
    neuropathyTypeOptions1() {
      const used = [this.section.neuropathy_type_2, this.section.neuropathy_type_3].filter(Boolean)
      return this.neuropathyTypes.filter(t => !used.includes(t.title))
    },
    neuropathyTypeOptions2() {
      const used = [this.section.neuropathy_type, this.section.neuropathy_type_3].filter(Boolean)
      return this.neuropathyTypes.filter(t => !used.includes(t.title))
    },
    neuropathyTypeOptions3() {
      const used = [this.section.neuropathy_type, this.section.neuropathy_type_2].filter(Boolean)
      return this.neuropathyTypes.filter(t => !used.includes(t.title))
    },
    isMedicalHistoriesRequired() {
      // Ensure medical_histories_other is a string and trim whitespace
      const trimmedValue = typeof this.section.medical_histories_other === 'string'
        ? this.section.medical_histories_other.trim()
        : ''
      // Return true if the trimmed value is empty, indicating that the field is required
      return !trimmedValue
    },
    // Evaluates if the section is missing required data
    isSectionIncomplete() {
      const hasSelectedHistories = this.section.medical_histories && this.section.medical_histories.length > 0
      const hasOtherHistory = typeof this.section.medical_histories_other === 'string' && this.section.medical_histories_other.trim().length > 0
      
      // Main past medical history check
      if (!hasSelectedHistories && !hasOtherHistory) {
        return true
      }

      // Neuropathy validation check (if diabetes is selected and neuropathy is checked)
      if (this.isHistoryDiabetes && this.section.neuropathy) {
        if (!this.section.neuropathy_type || !this.section.neuropathy_severity) {
          return true
        }
        if (this.section.neuropathy_type_2 && !this.section.neuropathy_severity_2) {
          return true
        }
        if (this.section.neuropathy_type_3 && !this.section.neuropathy_severity_3) {
          return true
        }
      }

      return false
    },
  },
  watch: {
    section: {
      deep: true,
      handler() {
        this.$emit('input', this.section.medical_histories)
        this.$emit('update:medical-histories-other', this.section.medical_histories_other)
        this.$emit('update:neuropathy', this.section.neuropathy)
        this.$emit('update:neuropathy-type', this.section.neuropathy_type)
        this.$emit('update:neuropathy-severity', this.section.neuropathy_severity)
        this.$emit('update:neuropathy-type-two', this.section.neuropathy_type_2)
        this.$emit('update:neuropathy-severity-two', this.section.neuropathy_severity_2)
        this.$emit('update:neuropathy-type-three', this.section.neuropathy_type_3)
        this.$emit('update:neuropathy-severity-three', this.section.neuropathy_severity_3)
      },
    },

    // Clear all neuropathy section if medical history does not include diabetes.
    'section.medical_histories': {
      handler() {
        if (!this.isHistoryDiabetes) {
          this.section.neuropathy = null
          this.section.neuropathy_type = null
          this.section.neuropathy_severity = null
        }
      },
    },

    'section.medical_histories_other': {
      handler() {
        if (!this.isHistoryDiabetes) {
          this.section.neuropathy = null
          this.section.neuropathy_type = null
          this.section.neuropathy_severity = null
        }
      },
    },
    'section.neuropathy_type': {
      handler(val) {
        if (!val) {
          this.section.neuropathy_severity = null
          this.section.neuropathy_type_2 = null
          this.section.neuropathy_severity_2 = null
          this.section.neuropathy_type_3 = null
          this.section.neuropathy_severity_3 = null
        }
      },
    },

    'section.neuropathy_type_2': {
      handler(val) {
        if (!val) {
          this.section.neuropathy_severity_2 = null
          this.section.neuropathy_type_3 = null
          this.section.neuropathy_severity_3 = null
        }
      },
    },

    'section.neuropathy_type_3': {
      handler(val) {
        if (!val) this.section.neuropathy_severity_3 = null
      },
    },

    'section.neuropathy': {
      handler(val) {
        if (!val) {
          this.section.neuropathy_type = null
          this.section.neuropathy_severity = null
          this.section.neuropathy_type_2 = null
          this.section.neuropathy_severity_2 = null
          this.section.neuropathy_type_3 = null
          this.section.neuropathy_severity_3 = null
        }
      },
    },
  },
  methods: {
  },
}
</script>

<style lang="scss" scoped>
.past-medical-history {
  margin-bottom: 15px !important;
  &.v-btn {
    width: 100%;
    margin-bottom: 19px !important;
  }
}
</style>
